import { IInteractionMessage, IWritingIssue } from "@edgetier/watchtower-types";
import { formatWritingIssueMessage } from "utilities-for/writing-issues/format-writing-issue-message";

/**
 * Only show a maximum of 100 characters before and after a writing issue in the message.
 * @param writingIssue The writing issue being formatted.
 * @param message      The full message.
 * @returns A message with a maximum of 100 characters before and after the writing issue.
 */
const clipAndFormatWritingIssueMessage = (writingIssue: IWritingIssue, message: string | undefined) => {
    if (typeof message === "undefined") {
        return undefined;
    }

    const formattedMessage = formatWritingIssueMessage([writingIssue], message);
    const lengthChange = formattedMessage.length - message.length;

    const { startIndex, endIndex } = writingIssue;
    const messageStartIndex = startIndex > 100 ? startIndex - (100 + lengthChange) : 0;
    const messageEndIndex =
        formattedMessage.length - endIndex > 100 ? endIndex + (100 + lengthChange) : formattedMessage.length;

    const clippedMessage = `${messageStartIndex > 0 ? "..." : ""}${formattedMessage.slice(
        messageStartIndex,
        messageEndIndex
    )}${messageEndIndex < formattedMessage.length ? "..." : ""}`;

    return clippedMessage;
};

/**
 * Create an array of writing issues with some extra data added from the interaction messages where the issue occurred.
 * @param messages      An array of interaction messages.
 * @param writingIssues An array of writing issues.
 * @returns An array of items with all of the writing issue properties and a few properties from interaction message.
 */
export const combineData = (messages?: IInteractionMessage[], writingIssues?: IWritingIssue[]) => {
    if (!Array.isArray(messages) || !Array.isArray(writingIssues)) {
        return null;
    }
    return writingIssues.map((issue) => {
        const message = messages.find(
            ({ interactionMessageId }) => interactionMessageId === issue.interactionMessageId
        );
        return {
            ...issue,
            interactionId: message?.interactionId,
            time: message?.sendDateTime,
            message: clipAndFormatWritingIssueMessage(issue, message?.message),
        };
    });
};
